import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Loader2, AlertCircle, ShieldCheck, Sparkles, ChevronDown, Upload } from 'lucide-react';
import PersonaVoicePlayButton from './PersonaVoicePlayButton';
import AiProgressSteps from './AiProgressSteps';

import {
  cleanTemplateTitle,
  type EvaluationCriterion,
  type InterviewTemplate,
  DEFAULT_PERSONA_ID,
  type Persona,
  type PersonaId,
  type TemplateCreatePayload,
  type TemplateDraft,
  type TemplateDifficulty,
  type TemplateDomain,
  type TemplateType,
} from '../../api/interview';

// All persona ids we still resolve in the backend (including retired
// ones like `mira` / `nora`) -- needed so editing an old template
// with a retired persona doesn't silently reset its persona to the
// default. New templates created via the picker only ever see
// non-hidden personas because they're filtered out of /api/v1/personas.
const ALL_PERSONA_IDS: readonly PersonaId[] = ['mira', 'lara', 'nora', 'iris', 'theo', 'erik', 'niko'];

interface TemplateBuilderModalProps {
  isOpen: boolean;
  personas: Persona[];
  /**
   * Optional template to use as a starting point. When set, all form
   * fields are pre-filled with values from this template and the
   * title gets a "(Custom)" / "(Anpassung)" suffix so the new
   * template is distinct from the original. `is_public` and
   * `family_key` are intentionally dropped -- a customised copy is
   * always private to the recruiter's company.
   */
  initial?: InterviewTemplate | null;
  /**
   * When true, `initial` is the template being EDITED (not cloned): fields
   * prefill without the "(Custom)" suffix and saving calls `onUpdate` instead
   * of `onCreate`. Only used for non-pool own templates.
   */
  isEdit?: boolean;
  onClose: () => void;
  onCreate: (payload: TemplateCreatePayload) => Promise<InterviewTemplate>;
  onUpdate?: (templateId: string, payload: TemplateCreatePayload) => Promise<InterviewTemplate>;
  /**
   * Optional: draft a template from a pasted job description (Agent 1).
   * When provided AND creating fresh (no `initial`), the builder shows an
   * "AI from job ad" block at the top. The returned draft pre-fills the
   * form for review/edit -- persona is intentionally left untouched.
   */
  onDraftFromJd?: (jobDescription: string, language?: 'de' | 'en') => Promise<TemplateDraft>;
  /** Upload a PDF/PNG/JPG job ad -> OCR/extracted text loaded into the box. */
  onExtractJobText?: (file: File) => Promise<string>;
}

interface DraftQuestion {
  question_text: string;
  question_type: 'open' | 'scenario' | 'coding' | 'followup_prompt';
  scoring_goal: string;     // "what a strong answer looks like" -> sent as expected_answer_hints
  notes: string;            // extra context for the AI scorer
  duration_seconds: number;
  sort_order: number;
  /** Optional carry-over tags from a customized template (e.g. step:setup). */
  tags?: string[];
}

const DEFAULT_CRITERIA: EvaluationCriterion[] = [
  { id: 'communication', label: 'Communication & Clarity', weight: 0.3, scale: '1-5', description: '' },
  { id: 'problem_solving', label: 'Problem Solving', weight: 0.3, scale: '1-5', description: '' },
  { id: 'ownership', label: 'Ownership & Drive', weight: 0.2, scale: '1-5', description: '' },
  { id: 'fit', label: 'Role Fit', weight: 0.2, scale: '1-5', description: '' },
];

const DEFAULT_QUESTION: DraftQuestion = {
  question_text: '',
  question_type: 'open',
  scoring_goal: '',
  notes: '',
  duration_seconds: 180,
  sort_order: 0,
};

const TemplateBuilderModal: React.FC<TemplateBuilderModalProps> = ({
  isOpen,
  personas,
  initial,
  isEdit = false,
  onClose,
  onCreate,
  onUpdate,
  onDraftFromJd,
  onExtractJobText,
}) => {
  const { i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<TemplateType>('behavioral');
  const [difficulty, setDifficulty] = useState<TemplateDifficulty>('mid');
  const [domain, setDomain] = useState<TemplateDomain>('general');
  const [duration, setDuration] = useState(30);
  const [personaId, setPersonaId] = useState<PersonaId>(DEFAULT_PERSONA_ID);
  const [enableCodingPanel, setEnableCodingPanel] = useState(false);
  const [enableWhiteboardPanel, setEnableWhiteboardPanel] = useState(false);
  const [enableNumbersPanel, setEnableNumbersPanel] = useState(false);
  const [enableNotepadPanel, setEnableNotepadPanel] = useState(false);
  const [criteria, setCriteria] = useState<EvaluationCriterion[]>(DEFAULT_CRITERIA);
  const [questions, setQuestions] = useState<DraftQuestion[]>([{ ...DEFAULT_QUESTION }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── AI "draft from job ad" (Agent 1) ──────────────────────
  // When a draft sets a specific output language (e.g. a German job ad in
  // an English UI), we honour it on submit instead of the UI language.
  const [jdOpen, setJdOpen] = useState(false);
  const [jdText, setJdText] = useState('');
  // 'auto' = let the AI follow the job ad's language; 'de'/'en' force it.
  const [jdLang, setJdLang] = useState<'auto' | 'de' | 'en'>('auto');
  const [jdUploading, setJdUploading] = useState(false);
  const jdFileRef = useRef<HTMLInputElement | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftApplied, setDraftApplied] = useState(false);
  const [draftLang, setDraftLang] = useState<'de' | 'en' | null>(null);

  // Hydrate the form from `initial` every time the modal opens with a
  // new template. We can't initialise these via `useState(() => ...)`
  // because the same modal instance is reused across multiple opens
  // (different templates on each click).
  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      const cleanTitle = cleanTemplateTitle(initial.title);
      // Editing keeps the original title; cloning adds a "(Custom)" suffix so
      // the new copy is distinct from the source.
      const suffix = isEdit ? '' : (lang === 'de' ? ' (Anpassung)' : ' (Custom)');
      setTitle(isEdit ? initial.title : cleanTitle + suffix);
      setDescription(initial.description || '');
      setType((initial.type as TemplateType) || 'behavioral');
      setDifficulty((initial.difficulty as TemplateDifficulty) || 'mid');
      setDomain((initial.domain as TemplateDomain) || 'general');
      setDuration(initial.duration_minutes || 30);
      const pid = (initial.persona_id as PersonaId) || DEFAULT_PERSONA_ID;
      setPersonaId(ALL_PERSONA_IDS.includes(pid) ? pid : DEFAULT_PERSONA_ID);
      setEnableCodingPanel(!!initial.enable_coding_panel);
      setEnableWhiteboardPanel(!!initial.enable_whiteboard_panel);
      setEnableNumbersPanel(!!initial.enable_numbers_panel);
      setEnableNotepadPanel(!!initial.enable_notepad_panel);
      setCriteria(
        (initial.evaluation_criteria || []).length > 0
          ? initial.evaluation_criteria.map((c) => ({ ...c }))
          : DEFAULT_CRITERIA,
      );
      const sortedQ = [...(initial.questions || [])].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );
      // Pool templates carry every case's steps in `questions` (e.g.
      // 5 cases x 5 steps = 25 entries). When a recruiter customizes
      // a pool we don't want to dump all 25 into the editor — they
      // expect to author ONE concrete interview. Pick the first case
      // (grouped by the `task:<id>` tag) and load just its 5 steps.
      // The pool/area tags get stripped so the new custom template is
      // a clean single-session template, not a one-case pool.
      const isPool = !!initial.sampling_strategy;
      const taskTagOf = (q: typeof sortedQ[number]): string | null => {
        const tag = (q.tags || []).find((tt) =>
          typeof tt === 'string' && tt.startsWith('task:'),
        );
        return tag || null;
      };
      const cleanTags = (tags: string[] | undefined): string[] => {
        if (!tags) return [];
        return tags.filter((tt) => {
          if (typeof tt !== 'string') return false;
          if (tt.startsWith('pool:')) return false;
          if (tt.startsWith('task:')) return false;
          if (tt.startsWith('area:')) return false;
          return true;
        });
      };
      let preparedQ = sortedQ;
      if (!isEdit && isPool && sortedQ.length > 0) {
        const firstTask = taskTagOf(sortedQ[0]);
        if (firstTask) {
          preparedQ = sortedQ.filter((q) => taskTagOf(q) === firstTask);
        }
      }
      setQuestions(
        preparedQ.length > 0
          ? preparedQ.map((q, idx) => ({
                question_text: q.question_text || '',
                question_type: (q.question_type as DraftQuestion['question_type']) || 'open',
                scoring_goal: q.expected_answer_hints || '',
                notes: '',
                duration_seconds: q.duration_seconds || 180,
                sort_order: idx,
                // Strip pool/task/area tags so the customized template
                // is a clean standalone, not still tied to the pool.
                tags: cleanTags(q.tags as string[] | undefined),
              }))
          : [{ ...DEFAULT_QUESTION }],
      );
    } else {
      // Reset to empty draft when opening fresh.
      setTitle('');
      setDescription('');
      setType('behavioral');
      setDifficulty('mid');
      setDomain('general');
      setDuration(30);
      setPersonaId(DEFAULT_PERSONA_ID);
      setEnableCodingPanel(false);
      setEnableWhiteboardPanel(false);
      setEnableNumbersPanel(false);
      setEnableNotepadPanel(false);
      setCriteria(DEFAULT_CRITERIA);
      setQuestions([{ ...DEFAULT_QUESTION }]);
    }
    setError(null);
    setSubmitting(false);
    setJdOpen(false);
    setJdText('');
    setJdLang('auto');
    setJdUploading(false);
    setDrafting(false);
    setDraftError(null);
    setDraftApplied(false);
    setDraftLang(null);
  }, [isOpen, initial, lang, isEdit]);

  const applyDraft = (d: TemplateDraft) => {
    setTitle((d.title || '').trim());
    setDescription((d.description || '').trim());
    setType((d.type as TemplateType) || 'behavioral');
    setDifficulty((d.difficulty as TemplateDifficulty) || 'mid');
    setDomain((d.domain as TemplateDomain) || 'general');
    setDuration(Math.max(5, Math.min(180, d.duration_minutes || 30)));
    setEnableCodingPanel(!!d.enable_coding_panel);
    setEnableWhiteboardPanel(!!d.enable_whiteboard_panel);
    setEnableNumbersPanel(!!d.enable_numbers_panel);
    setEnableNotepadPanel(!!d.enable_notepad_panel);
    setCriteria(
      (d.evaluation_criteria || []).length > 0
        ? d.evaluation_criteria.map((c) => ({ ...c }))
        : DEFAULT_CRITERIA,
    );
    setQuestions(
      (d.questions || []).length > 0
        ? d.questions.map((q, idx) => ({
            question_text: q.question_text || '',
            question_type: (q.question_type as DraftQuestion['question_type']) || 'open',
            scoring_goal: q.expected_answer_hints || '',
            notes: '',
            duration_seconds: q.duration_seconds || 180,
            sort_order: idx,
          }))
        : [{ ...DEFAULT_QUESTION }],
    );
    // Persona is intentionally NOT set by the AI -- recruiter's choice.
    setDraftLang(d.language === 'de' || d.language === 'en' ? d.language : null);
    setDraftApplied(true);
  };

  const handleJdFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onExtractJobText) return;
    const okType = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!okType) {
      setDraftError(lang === 'de' ? 'Bitte PDF, PNG oder JPG hochladen.' : 'Please upload a PDF, PNG or JPG.');
      return;
    }
    setDraftError(null);
    setJdUploading(true);
    try {
      const text = (await onExtractJobText(file) || '').trim();
      if (text.length < 20) {
        setDraftError(lang === 'de'
          ? 'Konnte keinen Text aus der Datei lesen - bitte einfügen.'
          : 'Could not read text from the file - please paste it.');
      } else {
        setJdText(text.slice(0, 16000));
      }
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : String(err));
    } finally {
      setJdUploading(false);
    }
  };

  const handleDraft = async () => {
    if (!onDraftFromJd) return;
    setDraftError(null);
    setDraftApplied(false);
    const text = jdText.trim();
    if (text.length < 20) {
      setDraftError(lang === 'de'
        ? 'Bitte mehr Text einfügen (mind. ein paar Sätze).'
        : 'Please paste a bit more text (a few sentences at least).');
      return;
    }
    setDrafting(true);
    try {
      const d = await onDraftFromJd(text, jdLang === 'auto' ? undefined : jdLang);
      applyDraft(d);
    } catch (e) {
      setDraftError(e instanceof Error ? e.message : String(e));
    } finally {
      setDrafting(false);
    }
  };

  if (!isOpen) return null;

  const t = {
    title:        isEdit
      ? (lang === 'de' ? 'Interview bearbeiten' : 'Edit interview')
      : initial
      ? (lang === 'de' ? 'Template anpassen' : 'Customize template')
      : (lang === 'de' ? 'Neues Interview-Template' : 'New interview template'),
    subtitleFromOriginal: (initial && !isEdit)
      ? (initial.sampling_strategy
          ? (lang === 'de'
              ? `Start von einem Beispiel-Case aus dem Pool "${cleanTemplateTitle(initial.title)}" - du erstellst daraus ein einzelnes, bearbeitbares Interview (kein Pool).`
              : `Starting from one example case of the pool "${cleanTemplateTitle(initial.title)}" - you're creating a single editable interview (not a pool).`)
          : (lang === 'de'
              ? `Abgeleitet von "${cleanTemplateTitle(initial.title)}"`
              : `Based on "${cleanTemplateTitle(initial.title)}"`))
      : null,
    titleLabel:   lang === 'de' ? 'Titel'                                 : 'Title',
    descLabel:    lang === 'de' ? 'Beschreibung (optional)'               : 'Description (optional)',
    typeLabel:    lang === 'de' ? 'Typ'                                   : 'Type',
    diffLabel:    lang === 'de' ? 'Level (für Scorecard)'                 : 'Level (for scorecard)',
    areaLabel:    lang === 'de' ? 'Bereich'                               : 'Area',
    durLabel:     lang === 'de' ? 'Dauer (Min.)'                          : 'Duration (min)',
    personaLabel: lang === 'de' ? 'AI-Interviewerin'                      : 'AI interviewer',
    critTitle:    lang === 'de' ? 'Bewertungskriterien'                   : 'Evaluation criteria',
    critHelp:     lang === 'de'
      ? 'Diese Kriterien steuern die AI-Scorecard. Gewichte müssen 1.00 ergeben.'
      : 'These criteria drive the AI scorecard. Weights must sum to 1.00.',
    critLabelHead: lang === 'de' ? 'Kriterium'                            : 'Criterion',
    critWeightHead: lang === 'de' ? 'Gewicht'                             : 'Weight',
    qTitle:       lang === 'de' ? 'Fragen'                                : 'Questions',
    qHelp:        lang === 'de'
      ? '"Ziel"-Texte werden der AI als Scoring-Kontext mitgegeben (nicht dem Kandidat:innen vorgelesen).'
      : '"Goal" texts are passed to the AI as scoring context (never read to candidates).'
    ,
    addCrit:      lang === 'de' ? 'Kriterium'                             : 'Criterion',
    addQuestion:  lang === 'de' ? 'Frage hinzufügen'                     : 'Add question',
    qGoal:        lang === 'de' ? 'Ziel der Frage / was eine starke Antwort enthält' : 'Question goal / what a strong answer looks like',
    qNotes:       lang === 'de' ? 'Zusatznotizen für die AI (optional)' : 'Extra notes for the AI (optional)',
    create:       isEdit
      ? (lang === 'de' ? 'Änderungen speichern' : 'Save changes')
      : (lang === 'de' ? 'Template erstellen' : 'Create template'),
    cancel:       lang === 'de' ? 'Abbrechen'                             : 'Cancel',
    busy:         isEdit
      ? (lang === 'de' ? 'Wird gespeichert...' : 'Saving...')
      : (lang === 'de' ? 'Wird erstellt...' : 'Creating...'),
    sumLabel:     lang === 'de' ? 'Summe'                                 : 'Sum',
    sumOk:        lang === 'de' ? 'passt'                                 : 'looks good',
    sumBad:       lang === 'de' ? 'soll 1.00 ergeben'                     : 'should equal 1.00',
    panelsTitle:  lang === 'de' ? 'Live-Panels im Interview'              : 'Live panels in interview',
    panelsHelp:   lang === 'de'
      ? 'Aktiviere zusätzliche Bereiche, die der/die Kandidat:in während des Gesprächs nutzen kann. Die AI sieht den Zustand mit und kann Follow-ups stellen.'
      : 'Enable extra surfaces the candidate can use while talking. The AI sees the live state and can ask follow-ups.',
    panelCoding:  lang === 'de' ? 'Live-Coding-Editor (Monaco)'           : 'Live coding editor (Monaco)',
    panelCodingDesc: lang === 'de'
      ? 'Mehrsprachiger Code-Editor mit Sprachwahl und Submit-Button.'
      : 'Multi-language code editor with language picker + submit.',
    panelWB:      lang === 'de' ? 'Whiteboard'                            : 'Whiteboard',
    panelWBDesc:  lang === 'de'
      ? 'Skizzen-Bereich, der sich automatisch an das Interview anpasst: System-Design-Komponenten, Klassen-Diagramme, User-Flows, Issue-Trees, Funnels, 2×2-Matrizen u.v.m. Wird der AI als Text-Zusammenfassung mitgegeben.'
      : 'Sketch surface that adapts to the interview: system-design components, class diagrams, user flows, issue trees, funnels, 2×2 matrices and more. Summarised as text for the AI.',
    panelNum:     lang === 'de' ? 'Zahlen-Pane (Mini-Spreadsheet)'        : 'Numbers pane (mini spreadsheet)',
    panelNumDesc: lang === 'de'
      ? 'Kleines Tabellen-Grid mit Formel-Support (=A1*B1, =SUM, =NPV, =IF). Ideal für Consulting-Case-Sizing, Finance-DCF, Pipeline-Math.'
      : 'Small grid with formula support (=A1*B1, =SUM, =NPV, =IF). Ideal for consulting case sizing, finance DCF, pipeline math.',
    panelNotepad: lang === 'de' ? 'Notizblock (Post-it)'                  : 'Notepad (post-it)',
    panelNotepadDesc: lang === 'de'
      ? 'Gelber Stift-Bereich für freie Notizen, S-T-A-R-Stories, Annahmen und Hypothesen. Die AI sieht den Text mit, reagiert aber nicht auf jeden Tipp.'
      : 'Yellow scratch surface for free-form notes, S-T-A-R stories, assumptions and hypotheses. The AI reads the text but won\'t react to every keystroke.',
    screenInfoTitle: lang === 'de' ? 'Bildschirmaufnahme: an'                : 'Screen recording: on',
    screenInfoBody:  lang === 'de'
      ? 'Standard für alle Cevee-Interviews. Kandidat:in teilt vor dem Start den Bildschirm; er wird mit der Webcam in eine Aufnahme komponiert (Picture-in-Picture). Details im AVV und Datenschutz.'
      : 'Standard for all Cevee interviews. The candidate shares their screen before the interview starts; it is composited with the webcam into a single recording (picture-in-picture). See DPA / Privacy Policy for details.',
    qType:        lang === 'de' ? 'Fragetyp'                              : 'Question type',
    qTypeOpen:    lang === 'de' ? 'Offen'                                 : 'Open',
    qTypeScenario:lang === 'de' ? 'Szenario'                              : 'Scenario',
    qTypeCoding:  lang === 'de' ? 'Coding'                                : 'Coding',
    qTypeFollowup:lang === 'de' ? 'Folgefrage'                            : 'Follow-up',
    diffHint:     lang === 'de'
      ? 'Standard-Level für die Scorecard. Kandidat:innen wählen beim Mock-Start ihr eigenes Level (Student-Senior).'
      : 'Default level for the scorecard. Candidates pick their own level (Student-Senior) when starting a mock.',
    jdTitle:      lang === 'de' ? 'Mit KI aus Stellenanzeige starten'      : 'Start with AI from a job ad',
    jdHint:       lang === 'de'
      ? 'Füge die Stellenanzeige ein - die KI macht daraus einen Entwurf (Typ, Level, Kriterien, Beispiel-Fragen). Du passt danach alles an. Die Persona wählst du selbst.'
      : 'Paste the job ad - the AI turns it into a draft (type, level, criteria, example questions). You adjust everything afterwards. You pick the persona yourself.',
    jdPlaceholder: lang === 'de' ? 'Stellenanzeige hier einfügen ...'      : 'Paste the job description here ...',
    jdBtn:        lang === 'de' ? 'Entwurf erstellen'                      : 'Create draft',
    jdBusy:       lang === 'de' ? 'Entwurf wird erstellt ...'              : 'Creating draft ...',
    jdApplied:    lang === 'de' ? 'Entwurf übernommen - bitte prüfen und anpassen.' : 'Draft applied - please review and adjust.',
    jdLangLabel:  lang === 'de' ? 'Sprache'                                : 'Language',
    jdLangAuto:   lang === 'de' ? 'Auto'                                   : 'Auto',
    jdLangHint:   lang === 'de' ? '(Auto folgt der Sprache der Anzeige)'   : '(Auto follows the job ad\u2019s language)',
    jdUpload:     lang === 'de' ? 'Datei hochladen (PDF/PNG/JPG)'          : 'Upload file (PDF/PNG/JPG)',
    jdUploadBusy: lang === 'de' ? 'Datei wird gelesen ...'                 : 'Reading file ...',
    jdThinkTitle: lang === 'de' ? 'KI liest die Anzeige ...'              : 'AI is reading the ad ...',
  };

  const draftSteps = lang === 'de'
    ? ['Stellenanzeige wird analysiert', 'Rolle, Level & Bereich erkannt', 'Format & Panels gewählt', 'Bewertungskriterien erstellt', 'Beispiel-Fragen formuliert']
    : ['Analysing the job ad', 'Detecting role, level & area', 'Choosing format & panels', 'Building evaluation criteria', 'Drafting example questions'];

  const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  const weightOk = Math.abs(totalWeight - 1) < 0.01;

  const updateCriterion = (i: number, patch: Partial<EvaluationCriterion>) =>
    setCriteria((arr) => arr.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const updateQuestion = (i: number, patch: Partial<DraftQuestion>) =>
    setQuestions((arr) => arr.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const handleSubmit = async () => {
    setError(null);
    if (!title.trim() || title.trim().length < 3) {
      setError(lang === 'de' ? 'Titel zu kurz' : 'Title too short');
      return;
    }
    if (questions.length === 0 || questions.some((q) => !q.question_text.trim())) {
      setError(lang === 'de' ? 'Bitte mind. 1 Frage mit Text' : 'Please add at least 1 question with text');
      return;
    }
    if (!weightOk) {
      setError(lang === 'de' ? 'Gewichte müssen 1.0 ergeben' : 'Weights must sum to 1.0');
      return;
    }
    setSubmitting(true);
    try {
      const payload: TemplateCreatePayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        difficulty,
        domain,
        duration_minutes: duration,
        language: draftLang || lang,
        persona_id: personaId,
        enable_coding_panel: enableCodingPanel,
        enable_whiteboard_panel: enableWhiteboardPanel,
        enable_numbers_panel: enableNumbersPanel,
        enable_notepad_panel: enableNotepadPanel,
        // Screen-share is always on for Cevee interviews -- consistent with
        // mocks, pool templates and the brand promise of low-fraud signal.
        // We deliberately don't expose a toggle for it in the builder.
        enable_screen_share: true,
        evaluation_criteria: criteria,
        questions: questions.map((q, idx) => {
          const goalParts = [q.scoring_goal.trim(), q.notes.trim() ? `Notes: ${q.notes.trim()}` : '']
            .filter(Boolean)
            .join('\n\n');
          return {
            question_text: q.question_text.trim(),
            question_type: q.question_type,
            expected_answer_hints: goalParts || undefined,
            sort_order: idx,
            duration_seconds: q.duration_seconds,
            tags: q.tags || [],
            is_required: true,
          };
        }),
      };
      if (isEdit && onUpdate && initial) {
        await onUpdate(initial.id, payload);
      } else {
        await onCreate(payload);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4">
      <div className="bg-white rounded-3xl shadow-[0_24px_60px_-12px_rgba(15,23,42,0.25)] w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col border border-slate-200/70">
        <div className="flex items-center justify-between px-7 py-5 border-b border-slate-200/60">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight">{t.title}</h2>
            {t.subtitleFromOriginal ? (
              <p className="text-xs text-pink-600 mt-0.5">{t.subtitleFromOriginal}</p>
            ) : (
              <p className="text-xs text-slate-500 mt-0.5">Cevee &middot; Interview Builder</p>
            )}
          </div>
          <button onClick={onClose} disabled={submitting} className="p-2 rounded-xl hover:bg-slate-100 disabled:opacity-50 transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-7 py-6 overflow-y-auto space-y-7">
          {/* ─── AI: draft from job ad (Agent 1) ────────────────────── */}
          {onDraftFromJd && !initial && (
            <section className="rounded-2xl border border-pink-200/70 bg-gradient-to-br from-pink-50/70 to-white">
              <button
                type="button"
                onClick={() => setJdOpen((v) => !v)}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-left"
              >
                <Sparkles className="w-4 h-4 text-pink-600 flex-shrink-0" />
                <span className="text-sm font-semibold text-slate-900 flex-1">{t.jdTitle}</span>
                <ChevronDown className={'w-4 h-4 text-slate-400 transition-transform ' + (jdOpen ? 'rotate-180' : '')} />
              </button>
              {jdOpen && (
                <div className="px-4 pb-4 space-y-3">
                  <p className="text-xs text-slate-500 leading-relaxed">{t.jdHint}</p>
                  {onExtractJobText && (
                    <div>
                      <input
                        ref={jdFileRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        className="hidden"
                        onChange={handleJdFile}
                        disabled={jdUploading || drafting}
                      />
                      <button
                        type="button"
                        onClick={() => jdFileRef.current?.click()}
                        disabled={jdUploading || drafting}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-pink-600 border border-slate-200 hover:border-pink-300 rounded-full px-3 py-1.5 transition disabled:opacity-50"
                      >
                        {jdUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {jdUploading ? t.jdUploadBusy : t.jdUpload}
                      </button>
                    </div>
                  )}
                  <textarea
                    value={jdText}
                    onChange={(e) => setJdText(e.target.value)}
                    rows={5}
                    className="input resize-none text-sm"
                    placeholder={t.jdPlaceholder}
                    disabled={drafting}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-500">{t.jdLangLabel}:</span>
                    {(['auto', 'de', 'en'] as const).map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setJdLang(l)}
                        disabled={drafting}
                        className={
                          'text-xs font-semibold px-2.5 py-1 rounded-full border transition disabled:opacity-50 ' +
                          (jdLang === l
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300')
                        }
                      >
                        {l === 'auto' ? t.jdLangAuto : l.toUpperCase()}
                      </button>
                    ))}
                    <span className="text-[11px] text-slate-400">{t.jdLangHint}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={handleDraft}
                      disabled={drafting || jdText.trim().length < 20}
                      className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-pink-600 hover:bg-pink-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                      {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {drafting ? t.jdBusy : t.jdBtn}
                    </button>
                    {draftApplied && !drafting && (
                      <span className="text-xs font-medium text-emerald-600">{t.jdApplied}</span>
                    )}
                  </div>
                  <AiProgressSteps running={drafting} title={t.jdThinkTitle} steps={draftSteps} stepMs={2200} />
                  {draftError && (
                    <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">{draftError}</p>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-4">
            <Field label={t.titleLabel}>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input"
                placeholder="Behavioral Screen - Senior Engineer"
              />
            </Field>
            <Field label={t.durLabel}>
              <input
                type="number"
                value={duration}
                min={5}
                max={180}
                onChange={(e) => setDuration(Math.max(5, Math.min(180, parseInt(e.target.value || '30', 10))))}
                className="input"
              />
            </Field>
          </div>

          <Field label={t.descLabel}>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="input resize-none"
              placeholder={lang === 'de' ? 'Wofür ist dieses Template gedacht?' : 'What is this template for?'}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label={t.typeLabel}>
              <select value={type} onChange={(e) => setType(e.target.value as TemplateType)} className="input">
                <option value="behavioral">Behavioral</option>
                <option value="technical">Technical (talk-only)</option>
                <option value="custom">Custom</option>
              </select>
            </Field>
            <Field label={t.diffLabel}>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as TemplateDifficulty)}
                className="input"
                title={t.diffHint}
              >
                <option value="student">{lang === 'de' ? 'Studi (Werkstud./Intern/Azubi)' : 'Student (intern/apprentice)'}</option>
                <option value="junior">Junior</option>
                <option value="mid">Mid</option>
                <option value="senior">Senior</option>
                <option value="staff">Staff</option>
              </select>
            </Field>
            <Field label={t.areaLabel}>
              <select
                value={domain}
                onChange={(e) => setDomain(e.target.value as TemplateDomain)}
                className="input"
              >
                <option value="general">{lang === 'de' ? 'Allgemein / Sonstiges' : 'General / Other'}</option>
                <option value="tech">Tech</option>
                <option value="product">Product</option>
                <option value="sales_gtm">Sales / GTM</option>
                <option value="operations">{lang === 'de' ? 'Operations / Logistik' : 'Operations / Logistics'}</option>
                <option value="skilled_trades">{lang === 'de' ? 'Handwerk / Bau' : 'Skilled trades'}</option>
                <option value="customer_service">{lang === 'de' ? 'Kundenservice' : 'Customer service'}</option>
                <option value="consulting">Consulting</option>
                <option value="marketing">Marketing</option>
                <option value="finance">Finance</option>
              </select>
            </Field>
          </div>

          {/* ─── Persona picker as cards with voice preview. Same look as
              the explorer mock modal, so candidates and recruiters share
              one mental model of who Mira/Lara/Nora/Iris are. */}
          <section>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h3 className="font-semibold text-slate-900 text-sm">{t.personaLabel}</h3>
              <p className="text-[11px] text-slate-400">
                {lang === 'de'
                  ? 'Klick auf das Lautsprechersymbol für eine Stimmprobe.'
                  : 'Tap the speaker icon for a voice sample.'}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {personas.map((p) => {
                const active = personaId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPersonaId(p.id as PersonaId)}
                    className={
                      'group rounded-2xl border px-3 py-2.5 text-left transition-colors ' +
                      (active
                        ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300')
                    }
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={
                          `w-8 h-8 rounded-full bg-gradient-to-br ${p.gradient || 'from-slate-400 to-slate-600'} ` +
                          'flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm'
                        }
                      >
                        {p.display_name[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={'text-xs font-semibold truncate ' + (active ? 'text-white' : 'text-slate-900')}>
                          {p.display_name}
                        </div>
                        <div className={'text-[10px] truncate ' + (active ? 'text-white/70' : 'text-slate-500')}>
                          {lang === 'de' ? p.vibe_de : p.vibe_en}
                        </div>
                      </div>
                      <PersonaVoicePlayButton
                        personaId={p.id}
                        language={lang === 'de' ? 'de' : 'en'}
                        displayName={p.display_name}
                        variant={active ? 'dark' : 'light'}
                        voiceSpeed={p.voice_speed}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ─── Live panels (coding / whiteboard) ──────────────────── */}
          <section>
            <div className="mb-2">
              <h3 className="font-semibold text-slate-900 text-sm">{t.panelsTitle}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t.panelsHelp}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PanelToggle
                checked={enableCodingPanel}
                onChange={setEnableCodingPanel}
                title={t.panelCoding}
                description={t.panelCodingDesc}
              />
              <PanelToggle
                checked={enableWhiteboardPanel}
                onChange={setEnableWhiteboardPanel}
                title={t.panelWB}
                description={t.panelWBDesc}
              />
              <PanelToggle
                checked={enableNumbersPanel}
                onChange={setEnableNumbersPanel}
                title={t.panelNum}
                description={t.panelNumDesc}
              />
              <PanelToggle
                checked={enableNotepadPanel}
                onChange={setEnableNotepadPanel}
                title={t.panelNotepad}
                description={t.panelNotepadDesc}
              />
            </div>
            {/* Screen-share is mandatory across the platform; we show
                it here as a read-only info pill so recruiters know it's
                always on without thinking they can disable it. */}
            <div className="mt-3 flex items-start gap-3 rounded-2xl border border-pink-200/60 bg-pink-50/40 px-3.5 py-2.5">
              <ShieldCheck className="w-4 h-4 text-pink-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-pink-900 leading-tight">
                  {t.screenInfoTitle}
                </p>
                <p className="text-xs text-pink-800/80 mt-0.5 leading-relaxed">
                  {t.screenInfoBody}
                </p>
              </div>
            </div>
          </section>

          {/* ─── Evaluation criteria ───────────────────────────────── */}
          <section>
            <div className="flex items-end justify-between gap-3 mb-2">
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">{t.critTitle}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t.critHelp}</p>
              </div>
              <button
                onClick={() => setCriteria((arr) => [...arr, { id: `crit_${arr.length}`, label: '', weight: 0.0, scale: '1-5', description: '' }])}
                className="text-xs text-pink-600 hover:text-pink-700 font-semibold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-pink-50"
              >
                <Plus className="w-3.5 h-3.5" />
                {t.addCrit}
              </button>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-slate-50/40 overflow-hidden">
              <div className="grid grid-cols-[1fr_88px_36px] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400 border-b border-slate-200/60 bg-white/50">
                <span>{t.critLabelHead}</span>
                <span className="text-center">{t.critWeightHead}</span>
                <span />
              </div>
              <div className="divide-y divide-slate-200/60">
                {criteria.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_88px_36px] gap-3 px-3 py-2 items-center">
                    <input
                      value={c.label}
                      onChange={(e) => updateCriterion(i, { label: e.target.value })}
                      placeholder="Communication & Clarity"
                      className="input-flat"
                    />
                    <input
                      type="number"
                      step={0.05}
                      min={0}
                      max={1}
                      value={c.weight}
                      onChange={(e) => updateCriterion(i, { weight: parseFloat(e.target.value || '0') })}
                      className="input-flat text-center font-mono"
                    />
                    <button
                      onClick={() => setCriteria((arr) => arr.filter((_, idx) => idx !== i))}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="remove criterion"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 bg-white/60 border-t border-slate-200/60 flex items-center justify-end gap-2 text-xs">
                <span className="text-slate-500">{t.sumLabel}:</span>
                <span className={['font-mono font-semibold', weightOk ? 'text-emerald-600' : 'text-amber-600'].join(' ')}>
                  {totalWeight.toFixed(2)}
                </span>
                <span className={weightOk ? 'text-emerald-600' : 'text-amber-600'}>
                  {weightOk ? `· ${t.sumOk}` : `· ${t.sumBad}`}
                </span>
              </div>
            </div>
          </section>

          {/* ─── Questions ──────────────────────────────────────────── */}
          <section>
            <div className="flex items-end justify-between gap-3 mb-2">
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">{t.qTitle}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t.qHelp}</p>
              </div>
              <button
                onClick={() => setQuestions((arr) => [...arr, { ...DEFAULT_QUESTION, sort_order: arr.length }])}
                className="text-xs text-pink-600 hover:text-pink-700 font-semibold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-pink-50"
              >
                <Plus className="w-3.5 h-3.5" />
                {t.addQuestion}
              </button>
            </div>
            <div className="space-y-3">
              {questions.map((q, i) => (
                <article key={i} className="rounded-2xl border border-slate-200/70 bg-white p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full flex-shrink-0 mt-1">
                      #{i + 1}
                    </span>
                    <textarea
                      value={q.question_text}
                      onChange={(e) => updateQuestion(i, { question_text: e.target.value })}
                      placeholder={lang === 'de' ? 'Welche Frage soll die AI stellen?' : 'What question should the AI ask?'}
                      rows={2}
                      className="input resize-none flex-1 font-medium"
                    />
                    <button
                      onClick={() => setQuestions((arr) => arr.filter((_, idx) => idx !== i))}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors self-start mt-1"
                      aria-label="remove question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="pl-12 grid grid-cols-1 gap-2">
                    <Field label={t.qGoal}>
                      <textarea
                        value={q.scoring_goal}
                        onChange={(e) => updateQuestion(i, { scoring_goal: e.target.value })}
                        rows={2}
                        placeholder={lang === 'de'
                          ? 'z.B. konkrete STAR-Struktur, klare Aktion + messbares Ergebnis, eigener Beitrag erkennbar'
                          : 'e.g. clear STAR structure, concrete action + measurable result, individual contribution visible'}
                        className="input resize-none text-sm"
                      />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_120px] gap-2">
                      <Field label={t.qNotes}>
                        <input
                          value={q.notes}
                          onChange={(e) => updateQuestion(i, { notes: e.target.value })}
                          placeholder={lang === 'de' ? 'Interner Hinweis für die AI...' : 'Internal hint for the AI...'}
                          className="input text-sm"
                        />
                      </Field>
                      <Field label={t.qType}>
                        <select
                          value={q.question_type}
                          onChange={(e) => updateQuestion(i, { question_type: e.target.value as DraftQuestion['question_type'] })}
                          className="input text-sm"
                        >
                          <option value="open">{t.qTypeOpen}</option>
                          <option value="scenario">{t.qTypeScenario}</option>
                          <option value="coding">{t.qTypeCoding}</option>
                          <option value="followup_prompt">{t.qTypeFollowup}</option>
                        </select>
                      </Field>
                      <Field label={lang === 'de' ? 'Antwortzeit' : 'Answer time'}>
                        <select
                          value={q.duration_seconds}
                          onChange={(e) => updateQuestion(i, { duration_seconds: parseInt(e.target.value, 10) })}
                          className="input text-sm"
                        >
                          <option value={60}>1 min</option>
                          <option value={120}>2 min</option>
                          <option value={180}>3 min</option>
                          <option value={240}>4 min</option>
                          <option value={300}>5 min</option>
                          <option value={420}>7 min</option>
                          <option value={600}>10 min</option>
                        </select>
                      </Field>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-7 py-4 border-t border-slate-200/60 bg-slate-50/60">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 disabled:opacity-50">
            {t.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? t.busy : t.create}
          </button>
        </div>

        <style>{`
          .input {
            display: block;
            width: 100%;
            padding: 0.6rem 0.85rem;
            font-size: 0.875rem;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 0.625rem;
            color: #0f172a;
            outline: none;
            transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
          }
          .input:focus {
            border-color: #ec4899;
            box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.12);
          }
          .input::placeholder { color: #94a3b8; }
          .input-flat {
            display: block;
            width: 100%;
            padding: 0.4rem 0.55rem;
            font-size: 0.875rem;
            background: transparent;
            border: 1px solid transparent;
            border-radius: 0.5rem;
            color: #0f172a;
            outline: none;
            transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
          }
          .input-flat:focus {
            background: white;
            border-color: #ec4899;
            box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.12);
          }
          .input-flat:hover {
            background: white;
          }
        `}</style>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
    {children}
  </label>
);

const PanelToggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  description: string;
}> = ({ checked, onChange, title, description }) => (
  <label
    className={[
      'flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors',
      checked
        ? 'bg-pink-50/60 border-pink-300/70 shadow-[inset_0_0_0_1px_rgba(236,72,153,0.15)]'
        : 'bg-white border-slate-200/70 hover:bg-slate-50/60',
    ].join(' ')}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 accent-pink-500 cursor-pointer"
    />
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5 leading-snug">{description}</div>
    </div>
  </label>
);

export default TemplateBuilderModal;
